import { IcdCorporationStatusRM, IcdCorporationManager } from "@herbjoy/apex-api-models";
import angular, { IPromise, IQService } from "angular";
import { Transition } from "angular-ui-router";
import { configureStates, resolveFromParam, RouteDeclaration } from "../../../core/router/router-helper.provider";
import { IcdCorporationService, sortIcdCorporationByAccess } from "../../../core/services/data-services/icd-corporation.service";
import { IcdCorporationDetail, IcdCorporationSelect } from "./icd-corporation-dashboard-types";
import { PermissionGrantService } from "../../../core/services/data-services/permission-grant.service";

const routes: RouteDeclaration[] = [{
    state: "corporations",
    config: {
        url: "/corporations",
        abstract: true
    }
},
{
    state: "corporations.dashboard",
    config: {
        url: "^/corporations/dashboard",
        data: {
            pageHeader: "Corporation Dashboard",
            permissionKey: "navigation:corporationDashboard"
        },
        params: {
            selectedTab: { type: "string", value: "" }
        },
        resolve: {
            selectedTab: resolveFromParam("selectedTab"),
            hubs: (hubService) => {
                'ngInject';
                return hubService.retrieveHubs()
            },
            auditors: (auditorService) => {
                'ngInject';
                return auditorService.retrieveAuditors()
            },
            icdCorporationStatuses: (icdCorporationService: IcdCorporationService) => {
                'ngInject';
                return icdCorporationService.retrieveIcdCorporationStatus();
            },
            icdCorporations: ($transition$, icdCorporationService: IcdCorporationService) => {
                'ngInject';
                const params = $transition$.params();
                return icdCorporationService.retrieveIcdCorporationsByModuleAdmin("Roster", {})
                    .then(icdCorporations => {
                        return icdCorporations.filter(icdCorporation => {
                            if (params.showOnlyEditablePayrolls) {
                                return icdCorporation.Access.CanUpdate;
                            }
                            return icdCorporation.Access.CanRead;
                        })
                            .sort(sortIcdCorporationByAccess)
                            .map(p => ({
                                Id: p.Id,
                                Text: `${p.Owner} - ${p.ClientName}`,
                                Access: p.Access
                            }));
                    });
            },
            icdCorporationID: ($transition$: Transition, icdCorporations: IcdCorporationSelect[]) => {
                'ngInject';
                const params = $transition$.params();
                if (params.icdCorporationID) {
                    return Number(params.icdCorporationID);
                } else if (icdCorporations && icdCorporations.length) {
                    return icdCorporations[0].Id;
                }
                return null;
            },
            icdCorporationDetails: ($q: IQService, icdCorporationService: IcdCorporationService,
                icdCorporations: IcdCorporationSelect[],
                icdCorporationID: number, auditors: IcdCorporationManager[],
                hubs: IcdCorporationManager[],
                icdCorporationStatuses: IcdCorporationStatusRM[]): IPromise<IcdCorporationDetail> => {
                'ngInject';
                if (!icdCorporationID) {
                    return $q.resolve(null);
                }
                const access = icdCorporations.find(p => p.Id === icdCorporationID).Access;
                return icdCorporationService.retrieveIcdCorporation(icdCorporationID)
                    .then(corporation => ({
                        ...corporation,
                        Access: access,
                        Hub: hubs.find(p => p.Id === corporation.HubID),
                        Auditors: corporation.Auditors.map(p => auditors.find(q => q.Id === p)),
                        Status: icdCorporationStatuses.find(p => p.Value === corporation.IcdCorporationStatusID)
                    }))
            },
            permissionGrants: (permissionGrantService: PermissionGrantService) => {
                'ngInject';
                return permissionGrantService.retrievePermissionGrants({});
            },
            onChangeTab: ($state, $transition$) => {
                'ngInject';
                return ["$event", ({ tab }) => {
                    const params = $transition$.params();
                    $state.go("corporations.dashboard.icdSelected.tabSelected", { ...params, selectedTab: tab.Value });
                }]
            },
            tabs: () => {
                return [
                    { Name: "Offices", Value: "offices" }
                ]
            },
        },
        redirectTo: (transition) => {
            const params = transition.params();
            return transition.injector().getAsync("icdCorporationID")
                .then(corporationID => {
                    return {
                        state: "corporations.dashboard.icdSelected.tabSelected",
                        params: { ...params, icdCorporationID: corporationID, selectedTab: "offices" }
                    }
                });
        }
    }
},
{
    state: "corporations.dashboard.icdSelected",
    config: {
        url: "^/corporations/:icdCorporationID/dashboard",
        redirectTo: (transition) => {
            const params = transition.params();
            return transition.injector().getAsync("icdCorporationID")
                .then(corporationID => {
                    return {
                        state: "corporations.dashboard.icdSelected.tabSelected",
                        params: { ...params, icdCorporationID: corporationID, selectedTab: "offices" }
                    }
                });
        }

    }
},
{
    state: "corporations.dashboard.icdSelected.tabSelected",
    config: {
        component: "apexIcdCorporationDashboardContainer",
        url: "^/corporations/:icdCorporationID/dashboard/:selectedTab",
        redirectTo: (transition) => {
            const params = transition.params();
            return {
                state: "corporations.dashboard.icdSelected.tabSelected." + params.selectedTab,
                params: { ...params, icdCorporationID: params.icdCorporationID, selectedTab: params.selectedTab }
            }
        }

    }
},
{
    state: "corporations.dashboard.icdSelected.tabSelected.offices",
    config: {
        resolve: {
            divisions: (divisionService) => {
                'ngInject';
                return divisionService.retrieveDivisions();
            },
            divisionProfiles: (divisionService) => {
                'ngInject';
                return divisionService.retrieveDivisionProfiles();
            },
            teams: (teamService) => {
                'ngInject';
                return teamService.retrieveTeams();
            },
            globalOfficeRoles: (roleService) => {
                'ngInject';
                return roleService.retrieveRoles()
                    .then(roles => {
                        return roles.filter(role => {
                            return role.GlobalRole && role.IsOfficeRole;
                        })
                    });
            },
            icdOffices: ($q, icdCorporationDetails, icdOfficeService) => {
                'ngInject';
                const requests = icdCorporationDetails.IcdOfficeIDs.map(p => {
                    return icdOfficeService.retrieveIcdOffice(p);
                });
                return $q.all(requests);
            }


        },
        views: {
            corporationDashboardContent: {
                component: "apexIcdCorporationDashboardOfficesDetails"
            }
        }
    }

}];

export const IcdCorporationDashboardRoutingModule = angular
    .module("apex.mainApp.corporation.dashboard.routing", [])
    .run(configureStates(routes))
    .name;
